import React, { useState, useEffect } from "react";
import { ArrowLeft, ArrowRight, Save2, TickCircle, Shop, Profile2User, Category, Ruler, TableDocument } from "iconsax-react";
import { motion, AnimatePresence } from "framer-motion";
import Loader from "../loading/Loader";
import MainButton from "../button/MainButton";
import UnitToggle from "../shared/UnitToggle";
import TextField from "../fields/TextField";
import { CM_TO_INCH, INCH_TO_CM } from "../../contexts/BodyMeasurementServiceContext";
import { useAuth } from "../../contexts/AuthContext";
import { useGlobalInfo } from "../../contexts/GlobalInfoContext";
import { submitSizeChart, getBrandChart } from "../../utilities/api/sizeChartService";
import { extractChartData } from "../../utilities/helpers/sizeChartValidation";
import { enqueueSnackbar } from "notistack";
import SizeChartManager from "./SizeChartManager";

interface StepConfig {
  Key: string;
  Values: string[];
  AllowedOthers: boolean;
}

interface SizeEntry {
  Size: string;
  EuropeSize: string;
  [measurement: string]: number | [number, number] | string;
}

interface FormData {
  Brand?: string;
  Gender?: string;
  Category?: string;
  Size?: string;
  OptionalType?: string;
  Sizes?: SizeEntry[];
  MeasurementValues?: Record<string, number | [number, number]>;
  MeasurementTypes?: Record<string, "single" | "range">;
  MeasurementUnit?: "cm" | "inch";
  sizeConfig?: {
    sizes: string[];
    measurementConfig: StepConfig[];
  };
}

interface SizeChartFormProps {
  onSubmit?: (data: FormData) => void;
  onError?: (error: string) => void;
  className?: string;
}

const SizeChartForm: React.FC<SizeChartFormProps> = ({ onSubmit, onError, className = "" }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { accessToken } = useAuth();
  const { userInfo } = useGlobalInfo();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [hasExistingData, setHasExistingData] = useState<boolean | null>(null);

  const loadSizeConfigFromAPI = async (brand?: string, gender?: string, category?: string) => {
    try {
      console.log("Load size config for:", { brand, gender, category });
      return null;
    } catch (error) {
      console.error("Failed to load size config:", error);
      return null;
    }
  };

  const [formData, setFormData] = useState<FormData>({});
  const [configuration, setConfiguration] = useState<StepConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});

  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [customSizeValue, setCustomSizeValue] = useState<string>("");
  const [selectedMeasurements, setSelectedMeasurements] = useState<string[]>([]);
  const [sizeMeasurements, setSizeMeasurements] = useState<Record<string, string[]>>({});
  const [measurementData, setMeasurementData] = useState<Record<string, Record<string, number | [number, number] | "">>>({});
  const [measurementTypes, setMeasurementTypes] = useState<Record<string, "single" | "range">>({});
  const [selectedUnit, setSelectedUnit] = useState<"cm" | "inch">("cm");

  const convertValueForDisplay = (value: number): string => {
    if (selectedUnit === "inch") {
      return (value * CM_TO_INCH).toFixed(1);
    }
    return value.toString();
  };

  const convertInputValueToCm = (inputValue: string, currentUnit: "cm" | "inch"): number => {
    const numValue = parseFloat(inputValue);
    if (isNaN(numValue)) return 0;
    return currentUnit === "inch" ? numValue * INCH_TO_CM : numValue;
  };

  const findMatchingSuggestion = (suggestions: string[], inputValue: string): string | null => {
    if (!inputValue) return null;
    return suggestions.find((suggestion) => suggestion.toLowerCase() === inputValue.toLowerCase()) || null;
  };

  const isValueInSuggestions = (suggestions: string[], inputValue: string): boolean => {
    return findMatchingSuggestion(suggestions, inputValue) !== null;
  };

  const isSizeInSuggestions = (availableSizes: string[], size: string): boolean => {
    return availableSizes.includes(size);
  };

  const handleUnitChange = (newUnit: "cm" | "inch") => {
    if (newUnit === selectedUnit) return;
    setSelectedUnit(newUnit);
  };

  const handleSizeToggle = (size: string) => {
    const isSelected = selectedSizes.includes(size);
    let newSelectedSizes: string[];

    if (isSelected) {
      newSelectedSizes = selectedSizes.filter((s) => s !== size);

      const newMeasurementData = { ...measurementData };
      delete newMeasurementData[size];
      setMeasurementData(newMeasurementData);

      const newSizeMeasurements = { ...sizeMeasurements };
      delete newSizeMeasurements[size];
      setSizeMeasurements(newSizeMeasurements);
    } else {
      newSelectedSizes = [...selectedSizes, size];

      setSizeMeasurements((prev) => ({
        ...prev,
        [size]: [],
      }));
    }

    setSelectedSizes(newSelectedSizes);
  };

  const handleCustomSizeAdd = () => {
    if (customSizeValue.trim() && !selectedSizes.includes(customSizeValue.trim())) {
      const newSize = customSizeValue.trim();
      setSelectedSizes((prev) => [...prev, newSize]);
      setSizeMeasurements((prev) => ({
        ...prev,
        [newSize]: [],
      }));
      setCustomSizeValue("");
    }
  };

  const handleSizeMeasurementToggle = (size: string, measurement: string) => {
    const currentMeasurements = sizeMeasurements[size] || [];
    const isSelected = currentMeasurements.includes(measurement);

    let newMeasurements: string[];
    if (isSelected) {
      newMeasurements = currentMeasurements.filter((m) => m !== measurement);

      const newMeasurementData = { ...measurementData };
      if (newMeasurementData[size]) {
        delete newMeasurementData[size][measurement];
      }
      setMeasurementData(newMeasurementData);

      const newMeasurementTypes = { ...measurementTypes };
      delete newMeasurementTypes[`${size}-${measurement}`];
      setMeasurementTypes(newMeasurementTypes);
    } else {
      newMeasurements = [...currentMeasurements, measurement];

      setMeasurementTypes((prev) => ({
        ...prev,
        [`${size}-${measurement}`]: "single",
      }));

      const newMeasurementData = { ...measurementData };
      if (!newMeasurementData[size]) {
        newMeasurementData[size] = {};
      }
      newMeasurementData[size][measurement] = "";
      setMeasurementData(newMeasurementData);
    }

    setSizeMeasurements((prev) => ({
      ...prev,
      [size]: newMeasurements,
    }));
  };

  const handleSizeMeasurementTypeChange = (size: string, measurement: string, type: "single" | "range") => {
    setMeasurementTypes((prev) => ({
      ...prev,
      [`${size}-${measurement}`]: type,
    }));

    const newMeasurementData = { ...measurementData };
    if (newMeasurementData[size]) {
      newMeasurementData[size][measurement] = "";
    }
    setMeasurementData(newMeasurementData);
  };

  const handleMeasurementValueChange = (size: string, measurement: string, value: string | number, isRangeEnd = false) => {
    const newMeasurementData = { ...measurementData };
    const numericValue = typeof value === "string" ? parseFloat(value) || 0 : value;
    const cmValue = convertInputValueToCm(numericValue.toString(), selectedUnit);

    if (!newMeasurementData[size]) {
      newMeasurementData[size] = {};
    }

    const measurementKey = `${size}-${measurement}`;
    if (measurementTypes[measurementKey] === "range") {
      const currentValue = newMeasurementData[size][measurement] as [number, number] | "";
      const currentArray: [number, number] = Array.isArray(currentValue) ? [currentValue[0] || 0, currentValue[1] || 0] : [0, 0];

      if (isRangeEnd) {
        currentArray[1] = cmValue;
      } else {
        currentArray[0] = cmValue;
      }

      newMeasurementData[size][measurement] = currentArray;
    } else {
      newMeasurementData[size][measurement] = cmValue;
    }

    setMeasurementData(newMeasurementData);
  };

  useEffect(() => {
    if (selectedMeasurements.length > 0 && selectedSizes.length > 0) {
      const newMeasurementData: Record<string, Record<string, number | [number, number] | "">> = {};

      selectedSizes.forEach((size) => {
        newMeasurementData[size] = {};
        selectedMeasurements.forEach((measurement) => {
          newMeasurementData[size][measurement] = "";
        });
      });

      setMeasurementData(newMeasurementData);
    }
  }, [selectedMeasurements, selectedSizes]);

  useEffect(() => {
    const loadConfig = async () => {
      if (formData.Brand && formData.Gender && formData.Category) {
        const config = await loadSizeConfigFromAPI(formData.Brand, formData.Gender, formData.Category);
        if (config) {
          setFormData((prev) => ({
            ...prev,
            sizeConfig: config,
          }));
        }
      }
    };

    loadConfig();
  }, [formData.Brand, formData.Gender, formData.Category]);

  const checkExistingData = React.useCallback(async () => {
    if (!accessToken || !userInfo?.id) return;

    try {
      setLoading(true);
      const response = await getBrandChart(accessToken, String(userInfo.id));

      const chartData = extractChartData(response);
      if (chartData) {
        setHasExistingData(true);
      } else {
        setHasExistingData(false);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Error checking existing data:", error);
      setHasExistingData(false);
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  }, [accessToken, userInfo?.id]);

  useEffect(() => {
    checkExistingData();
  }, []);

  useEffect(() => {
    const mockConfig: StepConfig[] = [
      {
        Key: "Gender",
        Values: ["Women", "Men", "GenderFree"],
        AllowedOthers: false,
      },
      {
        Key: "Category",
        Values: [
          "Clothing",
          "Bottoms",
          "Shorts",
          "Tops",
          "Pants",
          "Skirts",
          "CoatAndJacket",
          "Tops / Dresses",
          "Pants / Skirts / Shorts",
          "Denim",
          "Tops / Outerwear",
          "Outerwear",
          "DressesAndJumpsuits",
          "Jeans",
          "WovenPants",
          "Bra",
          "Underwear",
          "ShapeWear",
          "Bodysuit",
          "ActiveTops / SleepTops",
          "ActiveTops",
          "SleepTops",
          "SleepTops / SleepBottoms",
          "SleepBottoms",
          "MaternityLegging",
          "Nightgowns",
          "DreamShort",
          "Robes",
          "ClassicOnePieceSwimsuit",
          "CapSleeveOnePieceSwimsuit",
          "SwimBottoms",
          "Sarong",
          "OvercastJacket",
          "WesterlyCoachJacket",
          "BaseTee",
        ],
        AllowedOthers: true,
      },
      {
        Key: "Size",
        Values: ["US", "EUR"],
        AllowedOthers: true,
      },
      {
        Key: "Measurements",
        Values: ["Shoulder", "Chest", "Waist", "Hips", "Neck", "UnderChest", "Wrist", "Knee", "UpperBody", "LowerBody", "ArmLength"],
        AllowedOthers: false,
      },
    ];

    if (showForm) {
      setTimeout(() => {
        setConfiguration(mockConfig);
      }, 500);
    }
  }, [showForm]);

  const steps = [
    {
      key: "Brand",
      title: "Brand Information",
      description: "Enter the brand name for your size chart",
      svg: <Shop variant="Broken" />,
    },
    {
      key: "Gender",
      title: "Target Gender",
      description: "Select the target gender for this size chart",
      svg: <Profile2User variant="Broken" />,
    },
    {
      key: "Category",
      title: "Product Category",
      description: "Choose the clothing category",
      svg: <Category variant="Broken" />,
    },
    {
      key: "Size",
      title: "Size Standard",
      description: "Select the size measurement standard",
      svg: <Ruler variant="Broken" />,
    },
    {
      key: "SizeSelection",
      title: "Size Selection",
      description: "Select which sizes to include in your chart",
      svg: <TableDocument variant="Broken" />,
    },
    {
      key: "SizeChart",
      title: "Size Chart",
      description: "Create your comprehensive size chart with measurements",
      svg: <TableDocument variant="Broken" />,
    },
  ];

  const validateStep = (stepIndex: number): boolean => {
    const errors = { ...validationErrors };

    switch (stepIndex) {
      case 0:
        if (!formData.Brand?.trim()) {
          errors[stepIndex] = "Brand name is required";
          setValidationErrors(errors);
          return false;
        }
        break;
      case 1:
        if (!formData.Gender?.trim()) {
          errors[stepIndex] = "Please select a gender";
          setValidationErrors(errors);
          return false;
        }
        break;
      case 2:
        if (!formData.Category?.trim()) {
          errors[stepIndex] = "Please select a category";
          setValidationErrors(errors);
          return false;
        }
        break;
      case 3:
        if (!formData.Size?.trim()) {
          errors[stepIndex] = "Please select a size standard";
          setValidationErrors(errors);
          return false;
        }
        break;
      case 4:
        if (selectedSizes.length === 0) {
          errors[stepIndex] = "Please select at least one size";
          setValidationErrors(errors);
          return false;
        }
        break;
      case 5:
        for (const size of selectedSizes) {
          let hasMeasurementForThisSize = false;

          if (measurementData[size]) {
            for (const measurement in measurementData[size]) {
              const value = measurementData[size][measurement];
              if (value !== "" && value !== undefined) {
                hasMeasurementForThisSize = true;
                break;
              }
            }
          }

          if (!hasMeasurementForThisSize) {
            errors[stepIndex] = `Size "${size}" must have at least one measurement value. Please add measurements for all selected sizes.`;
            setValidationErrors(errors);
            return false;
          }
        }

        for (const size of selectedSizes) {
          if (measurementData[size]) {
            for (const measurement in measurementData[size]) {
              const value = measurementData[size][measurement];
              if (measurementTypes[measurement] === "range" && Array.isArray(value) && value[0] !== 0 && value[1] !== 0) {
                if (value[0] >= value[1]) {
                  errors[stepIndex] = `Range values for ${measurement} in size ${size} are invalid (minimum should be less than maximum)`;
                  setValidationErrors(errors);
                  return false;
                }
              }
            }
          }
        }
        break;
    }

    delete errors[stepIndex];
    setValidationErrors(errors);
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep) && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFieldChange = (key: string, value: any, suggestions?: string[]) => {
    let finalValue = value;

    if (suggestions && typeof value === "string") {
      const matchingSuggestion = findMatchingSuggestion(suggestions, value);
      if (matchingSuggestion) {
        finalValue = matchingSuggestion;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [key]: finalValue,
    }));

    const errors = { ...validationErrors };
    delete errors[currentStep];
    setValidationErrors(errors);
  };

  const handleAddNew = () => {
    setShowForm(true);
    // Don't set hasExistingData to false - we need it to show the back button
    setCurrentStep(0);
    setFormData({});
    setSelectedSizes([]);
    setCustomSizeValue("");
    setSelectedMeasurements([]);
    setSizeMeasurements({});
    setMeasurementData({});
    setMeasurementTypes({});
  };

  const handleBackToManager = () => {
    setShowForm(false);
    setHasExistingData(true);
    checkExistingData();
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    if (!accessToken) {
      enqueueSnackbar("Authentication required", { variant: "error" });
      return;
    }

    try {
      setIsSubmitting(true);

      if (!userInfo?.id) {
        enqueueSnackbar("Something went wrong - this service is not available", { variant: "error" });
        return;
      }

      const sizes: any[] = [];

      const allSizes = ["0 / XXXS", "2 / XXS", "4 / XS", "6 / S", "8 / M", "10 / L", "12 / XL", "14 / XL", "16 / 1X", "18 / 1X", "20 / 2X"];
      const europeSizes = ["26", "28", "32", "36", "40", "44", "46", "48", "50", "52", "54"];

      selectedSizes.forEach((size) => {
        const sizeIndex = allSizes.indexOf(size);
        const sizeEntry: any = {
          Size: size,
          EuropeSize: europeSizes[sizeIndex] || (26 + sizeIndex * 2).toString(),
        };

        let hasMeasurementForThisSize = false;

        const sizeSpecificMeasurements = sizeMeasurements[size] || [];
        sizeSpecificMeasurements.forEach((measurement) => {
          const value = measurementData[size]?.[measurement];
          if (value !== "" && value !== undefined) {
            const measurementKey = `${size}-${measurement}`;
            if (measurementTypes[measurementKey] === "range" && Array.isArray(value)) {
              sizeEntry[measurement] = value;
            } else if (typeof value === "number") {
              sizeEntry[measurement] = value;
            } else if (typeof value === "string") {
              sizeEntry[measurement] = parseFloat(value) || 0;
            }
            hasMeasurementForThisSize = true;
          }
        });

        if (hasMeasurementForThisSize) {
          sizes.push(sizeEntry);
        }
      });

      const finalData: any = {
        Brand: String(userInfo.id),
        Gender: formData.Gender!,
        Category: formData.Category!,
        Size: formData.Size!,
        OptionalType: "Regular",
        Sizes: sizes,
      };

      const response = await submitSizeChart(accessToken, finalData);

      if (response.ok && response.status_code === 200) {
        enqueueSnackbar("Size chart saved successfully!", { variant: "success" });
        onSubmit?.(finalData);

        setCurrentStep(0);
        setFormData({});
        setSelectedSizes([]);
        setCustomSizeValue("");
        setSelectedMeasurements([]);
        setSizeMeasurements({});
        setMeasurementData({});
        setMeasurementTypes({});

        setTimeout(() => {
          handleBackToManager();
        }, 1000);
      } else {
        enqueueSnackbar("Something went wrong. Please try again.", { variant: "error" });

        if (response.status_code === -2 && response.data && Array.isArray(response.data)) {
          response.data.forEach((errorMessage: string) => {
            enqueueSnackbar(errorMessage, { variant: "error" });
          });
        }

        onError?.(response.message || "API Error");
      }
    } catch (error: any) {
      console.error("Error submitting size chart:", error);
      const errorMessage = error.message || "Failed to save size chart";
      enqueueSnackbar(errorMessage, { variant: "error" });
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepNavigator = () => (
    <div className="flex justify-center w-full">
      <div className="flex justify-center p-4 overflow-hidden md:p-4 md:px-6 w-fit">
        {steps.map((step, index) => (
          <div key={index} className={`relative flex rounded-full items-center justify-center *:flex *:flex-col`}>
            <div
              className={`flex flex-col items-center gap-2 min-w-[3.5rem] sm:min-w-[5rem] md:min-w-28 lg:min-w-32 xl:min-w-40 ${
                currentStep === index || currentStep > index ? "text-shindig-300" : "text-gorgonzola-blue-300/70"
              }`}
            >
              {index !== steps.length - 1 ? (
                <span className={`absolute top-6 translate-x-1/2 w-full h-0.5 z-10 ${currentStep === index || currentStep > index ? "bg-shindig-300" : "bg-gorgonzola-blue-300/70"}`} />
              ) : null}

              <div
                className={`size-12 bg-white grid place-content-center border rounded-full z-20 relative ${
                  currentStep === index || currentStep > index ? "border-shindig-300" : "border-gorgonzola-blue-300/70"
                }`}
              >
                <span>{React.cloneElement(step.svg, { className: "size-6" })}</span>

                {validationErrors[index] && (
                  <div className="absolute z-20 flex items-center justify-center w-4 h-4 rounded-full -top-1 -right-1 bg-english-vermillion-300">
                    <span className="text-xs font-bold text-white">!</span>
                  </div>
                )}
              </div>

              <span className="hidden md:block capitalize text-[13px] lg:text-xs">{step.title}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderBrandStep = () => (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="space-y-6">
      <div className="space-y-1 text-center">
        <h3 className="text-lg font-inter-bold text-closed-shutter-300">{steps[0].title}</h3>
        <p className="text-base text-closed-shutter-200 font-inter-regular">{steps[0].description}</p>
      </div>

      <div className="max-w-md mx-auto space-y-4">
        <TextField
          label="Brand Name"
          placeholder="Nike, Adidas, Uniqlo, etc..."
          value={formData.Brand || ""}
          onChange={(e) => handleFieldChange("Brand", (e.target as HTMLInputElement).value)}
          required={true}
          error={!!validationErrors[0]}
          success={!validationErrors[0] && !!formData.Brand}
        />

        {validationErrors[0] && (
          <p className="flex items-center text-xs text-english-vermillion-400 font-inter-regular">
            <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {validationErrors[0]}
          </p>
        )}
      </div>
    </motion.div>
  );

  const renderSelectionStep = (stepConfig: StepConfig, stepIndex: number) => (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="space-y-6">
      <div className="space-y-1 text-center">
        <h3 className="text-lg font-inter-bold text-closed-shutter-300">{steps[stepIndex].title}</h3>
        <p className="text-xs text-closed-shutter-200 font-inter-regular">{steps[stepIndex].description}</p>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">
        {/* Show selected value with clear option */}
        {formData[stepConfig.Key as keyof FormData] && (
          <div className="p-3 border rounded-lg bg-gorgonzola-blue-50 border-gorgonzola-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <TickCircle className="w-4 h-4 mr-2 text-gorgonzola-blue-400" variant="Bold" />
                <span className="text-sm text-gorgonzola-blue-600 font-inter-medium">Selected: {String(formData[stepConfig.Key as keyof FormData])}</span>
              </div>
              <button
                onClick={() => handleFieldChange(stepConfig.Key, "")}
                className="flex items-center justify-center w-6 h-6 transition-colors rounded-full text-gorgonzola-blue-400 hover:bg-gorgonzola-blue-200 hover:text-gorgonzola-blue-600"
                title="Clear selection"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <label className="block text-xs text-closed-shutter-300">
            Choose from suggestions <span className="text-english-vermillion-300">*</span>
          </label>

          <div className={`grid gap-3 max-h-64 overflow-y-auto custom_scroll ${stepConfig.Values.length > 6 ? "grid-cols-2 md:grid-cols-3 2xl:grid-cols-4" : "grid-cols-2 sm:grid-cols-3"}`}>
            {stepConfig.Values.map((value) => {
              const isSelected = formData[stepConfig.Key as keyof FormData] === value;
              return (
                <button
                  key={value}
                  onClick={() => handleFieldChange(stepConfig.Key, value)}
                  className={`px-4 py-3 text-xs font-inter-medium border rounded-lg transition-all duration-600 text-left ${
                    isSelected
                      ? "bg-gorgonzola-blue-100 border-gorgonzola-blue-300 text-gorgonzola-blue-300"
                      : "bg-white border-closed-shutter-200 text-closed-shutter-300 hover:border-gorgonzola-blue-100 hover:bg-gorgonzola-blue-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="line-clamp-2">{value}</span>
                    {isSelected && <TickCircle className="flex-shrink-0 w-4 h-4 ml-2" variant="Bold" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {stepConfig.AllowedOthers && (
          <div className="pt-4 border-t border-gorgonzola-blue-50">
            <div className="space-y-3">
              <TextField
                label="Or enter custom value"
                placeholder={`Enter custom ${stepConfig.Key.toLowerCase()}`}
                value={isValueInSuggestions(stepConfig.Values, formData[stepConfig.Key as keyof FormData] as string) ? "" : (formData[stepConfig.Key as keyof FormData] as string) || ""}
                onChange={(e) => handleFieldChange(stepConfig.Key, (e.target as HTMLInputElement).value, stepConfig.Values)}
                success={!!(formData[stepConfig.Key as keyof FormData] && !isValueInSuggestions(stepConfig.Values, formData[stepConfig.Key as keyof FormData] as string))}
              />

              {formData[stepConfig.Key as keyof FormData] && !isValueInSuggestions(stepConfig.Values, formData[stepConfig.Key as keyof FormData] as string) && (
                <div className="p-3 border rounded-lg bg-gorgonzola-blue-50 border-gorgonzola-blue-200">
                  <p className="flex items-center text-xs text-gorgonzola-blue-400 font-inter-medium">
                    <TickCircle className="w-4 h-4 mr-1.5" variant="Bold" />
                    Custom value will be used
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {validationErrors[stepIndex] && (
          <div className="text-center">
            <p className="flex items-center justify-center text-xs text-english-vermillion-400 font-inter-regular">
              <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {validationErrors[stepIndex]}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );

  const renderSizeSelectionStep = () => {
    const availableSizes = ["0 / XXXS", "2 / XXS", "4 / XS", "6 / S", "8 / M", "10 / L", "12 / XL", "14 / XL", "16 / 1X", "18 / 1X", "20 / 2X"];

    return (
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="space-y-6">
        <div className="space-y-1 text-center">
          <h3 className="text-lg font-inter-bold text-closed-shutter-300">{steps[4].title}</h3>
          <p className="text-xs text-closed-shutter-200 font-inter-regular">{steps[4].description}</p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Show selected sizes with clear option */}
          {selectedSizes.length > 0 && (
            <div className="p-3 border rounded-lg bg-gorgonzola-blue-50 border-gorgonzola-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gorgonzola-blue-600 font-inter-medium">Selected Sizes:</span>
                <button
                  onClick={() => {
                    setSelectedSizes([]);
                    setSizeMeasurements({});
                    setMeasurementData({});
                  }}
                  className="flex items-center justify-center w-6 h-6 transition-colors rounded-full text-gorgonzola-blue-400 hover:bg-gorgonzola-blue-200 hover:text-gorgonzola-blue-600"
                  title="Clear all selections"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedSizes.map((size) => (
                  <div key={size} className="flex items-center px-2 py-1 text-xs rounded-full bg-gorgonzola-blue-200 text-gorgonzola-blue-700">
                    <span>{size}</span>
                    <button onClick={() => handleSizeToggle(size)} className="ml-1 hover:text-gorgonzola-blue-900">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-3">
              <label className="block text-xs text-closed-shutter-300">
                Choose from suggestions <span className="text-english-vermillion-300">*</span>
              </label>

              <div className="grid grid-cols-2 gap-3 overflow-y-auto md:grid-cols-3 lg:grid-cols-4 max-h-64 custom_scroll">
                {availableSizes.map((size) => {
                  const isSelected = selectedSizes.includes(size);
                  return (
                    <button
                      key={size}
                      onClick={() => handleSizeToggle(size)}
                      className={`px-4 py-3 text-xs font-inter-medium border rounded-lg transition-all duration-600 text-left ${
                        isSelected
                          ? "bg-gorgonzola-blue-100 border-gorgonzola-blue-300 text-gorgonzola-blue-300"
                          : "bg-white border-closed-shutter-200 text-closed-shutter-300 hover:border-gorgonzola-blue-100 hover:bg-gorgonzola-blue-50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="line-clamp-2">{size}</span>
                        {isSelected && <TickCircle className="flex-shrink-0 w-4 h-4 ml-2" variant="Bold" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom size input */}
            <div className="pt-4 border-t border-gorgonzola-blue-50">
              <div className="space-y-3">
                <TextField
                  label="Or enter custom size"
                  placeholder="Enter custom size (e.g., 22 / 3X, Medium-Large, etc.)"
                  value={customSizeValue}
                  onChange={(e) => setCustomSizeValue((e.target as HTMLInputElement).value)}
                  success={!!customSizeValue && !isSizeInSuggestions(availableSizes, customSizeValue)}
                />

                {customSizeValue.trim() && (
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-gorgonzola-blue-50 border-gorgonzola-blue-200">
                    <div className="flex items-center">
                      <TickCircle className="w-4 h-4 mr-1.5 text-gorgonzola-blue-400" variant="Bold" />
                      <p className="text-xs text-gorgonzola-blue-400 font-inter-medium">
                        {selectedSizes.includes(customSizeValue.trim()) ? `"${customSizeValue.trim()}" is already selected` : `Ready to add "${customSizeValue.trim()}"`}
                      </p>
                    </div>
                    {!selectedSizes.includes(customSizeValue.trim()) && (
                      <MainButton onClick={handleCustomSizeAdd} type="primary" customClass="px-3 py-1 text-xs">
                        Add Size
                      </MainButton>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {validationErrors[4] && (
            <div className="text-center">
              <p className="flex items-center justify-center text-xs text-english-vermillion-400 font-inter-regular">
                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationErrors[4]}
              </p>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const renderCombinedSizeChartStep = () => {
    const measurementConfig = configuration.find((c) => c.Key === "Measurements");
    if (!measurementConfig) return null;

    return (
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="space-y-6">
        <div className="space-y-1 text-center">
          <h3 className="text-lg font-inter-bold text-closed-shutter-300">{steps[5].title}</h3>
          <p className="text-xs text-closed-shutter-200 font-inter-regular">{steps[5].description}</p>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          <div className="flex flex-col items-center justify-between gap-2 mb-4 md:flex-row">
            <h4 className="text-base text-closed-shutter-300">Configure Size Chart</h4>
            <div className="flex items-center gap-2">
              <span className="text-sm text-closed-shutter-300">Unit:</span>
              <UnitToggle selectedUnit={selectedUnit} onUnitChange={handleUnitChange} />
            </div>
          </div>

          <div className="space-y-6">
            {selectedSizes.map((size) => (
              <div key={size} className="p-4 bg-white border rounded-lg">
                <h5 className="mb-4 text-lg font-inter-bold text-closed-shutter-300">Size: {size}</h5>

                {/* Measurement Selection for this size */}
                <div className="mb-4 space-y-3">
                  <h6 className="text-sm font-inter-bold text-closed-shutter-300">Select Measurements for this size:</h6>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
                    {measurementConfig.Values.map((measurement) => {
                      const isSelected = (sizeMeasurements[size] || []).includes(measurement);
                      return (
                        <button
                          key={measurement}
                          onClick={() => handleSizeMeasurementToggle(size, measurement)}
                          className={`px-3 py-2 text-xs font-inter-medium border rounded-lg transition-all duration-600 text-left ${
                            isSelected
                              ? "bg-gorgonzola-blue-100 border-gorgonzola-blue-300 text-gorgonzola-blue-300"
                              : "bg-white border-closed-shutter-200 text-closed-shutter-300 hover:border-gorgonzola-blue-100 hover:bg-gorgonzola-blue-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{measurement}</span>
                            {isSelected && <TickCircle className="w-3 h-3 ml-1" variant="Bold" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Measurement Values for this size */}
                {(sizeMeasurements[size] || []).length > 0 && (
                  <div className="space-y-4">
                    <h6 className="text-sm font-inter-bold text-closed-shutter-300">Enter Values ({selectedUnit}):</h6>
                    {(sizeMeasurements[size] || []).map((measurement) => (
                      <div key={`${size}-${measurement}`} className="p-3 border rounded-lg bg-gray-50">
                        <div className="flex flex-col items-center justify-between gap-2 mb-3 md:flex-row">
                          <span className="text-sm font-inter-bold text-closed-shutter-300">{measurement}</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleSizeMeasurementTypeChange(size, measurement, "single")}
                              className={`px-2 py-1 text-xs font-medium rounded-full transition-all border ${
                                measurementTypes[`${size}-${measurement}`] === "single"
                                  ? "bg-gorgonzola-blue-100 text-gorgonzola-blue-700 border-gorgonzola-blue-300"
                                  : "bg-gray-100 text-gray-600 hover:bg-gorgonzola-blue-50"
                              }`}
                            >
                              Single
                            </button>
                            <button
                              onClick={() => handleSizeMeasurementTypeChange(size, measurement, "range")}
                              className={`px-2 py-1 text-xs font-medium rounded-full transition-all border ${
                                measurementTypes[`${size}-${measurement}`] === "range"
                                  ? "bg-gorgonzola-blue-100 text-gorgonzola-blue-700 border-gorgonzola-blue-300"
                                  : "bg-gray-100 text-gray-600 hover:bg-gorgonzola-blue-50"
                              }`}
                            >
                              Range
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-col items-center w-full gap-3 md:flex-row">
                          {measurementTypes[`${size}-${measurement}`] === "range" ? (
                            <>
                              <div className="flex-1 w-full">
                                <TextField
                                  type="text"
                                  label={`Min (${selectedUnit})`}
                                  placeholder={selectedUnit === "cm" ? "e.g. 85" : "e.g. 33.5"}
                                  value={
                                    Array.isArray(measurementData[size]?.[measurement])
                                      ? (measurementData[size][measurement] as [number, number])[0] > 0
                                        ? convertValueForDisplay((measurementData[size][measurement] as [number, number])[0])
                                        : ""
                                      : ""
                                  }
                                  onChange={(e) => handleMeasurementValueChange(size, measurement, (e.target as HTMLInputElement).value, false)}
                                />
                              </div>
                              <div className="items-end hidden pb-2 md:flex">
                                <span className="text-sm text-gray-500">to</span>
                              </div>
                              <div className="flex-1 w-full">
                                <TextField
                                  type="text"
                                  label={`Max (${selectedUnit})`}
                                  placeholder={selectedUnit === "cm" ? "e.g. 95" : "e.g. 37.5"}
                                  value={
                                    Array.isArray(measurementData[size]?.[measurement])
                                      ? (measurementData[size][measurement] as [number, number])[1] > 0
                                        ? convertValueForDisplay((measurementData[size][measurement] as [number, number])[1])
                                        : ""
                                      : ""
                                  }
                                  onChange={(e) => handleMeasurementValueChange(size, measurement, (e.target as HTMLInputElement).value, true)}
                                />
                              </div>
                            </>
                          ) : (
                            <div className="flex-1 w-full">
                              <TextField
                                type="text"
                                label={`Value (${selectedUnit})`}
                                placeholder={selectedUnit === "cm" ? "e.g. 90" : "e.g. 35.5"}
                                value={
                                  typeof measurementData[size]?.[measurement] === "number"
                                    ? (measurementData[size][measurement] as number) > 0
                                      ? convertValueForDisplay(measurementData[size][measurement] as number)
                                      : ""
                                    : ""
                                }
                                onChange={(e) => handleMeasurementValueChange(size, measurement, (e.target as HTMLInputElement).value)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {validationErrors[5] && (
            <div className="text-center">
              <p className="flex items-center justify-center text-xs text-english-vermillion-400 font-inter-regular">
                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {validationErrors[5]}
              </p>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderBrandStep();
      case 1:
        return renderSelectionStep(configuration.find((c) => c.Key === "Gender")!, 1);
      case 2:
        return renderSelectionStep(configuration.find((c) => c.Key === "Category")!, 2);
      case 3:
        return renderSelectionStep(configuration.find((c) => c.Key === "Size")!, 3);
      case 4:
        return renderSizeSelectionStep();
      case 5:
        return renderCombinedSizeChartStep();
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col w-full overflow-hidden border divide-y divide-gorgonzola-blue-50 rounded-xl border-gorgonzola-blue-50">
        <div className="p-4 md:p-6">
          <h2 className="font-bold text-closed-shutter-300">Size Chart</h2>
        </div>
        <div className="flex items-center justify-center h-64">
          <Loader />
        </div>
      </div>
    );
  }

  if (hasExistingData && !showForm) {
    return <SizeChartManager onAddNew={handleAddNew} className={className} />;
  }

  return (
    <div className={`flex flex-col justify-between w-full h-full overflow-hidden overflow-y-auto border divide-y divide-gorgonzola-blue-50 rounded-xl border-gorgonzola-blue-50 ${className}`}>
      <div className="p-4 md:p-6">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <h2 className="font-bold text-closed-shutter-300">{hasExistingData ? "Add New Size Chart" : "Size Chart"}</h2>
          </div>
          <div>
            <div className="px-3 py-1 text-xs rounded-full bg-gorgonzola-blue-100 text-gorgonzola-blue-300 font-inter-medium">
              Step {currentStep + 1} of {steps.length}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 w-full md:space-y-4 lg:space-y-6 xl:space-y-8">
        <div className="">{renderStepNavigator()}</div>

        <div className="flex-1 p-4">
          <AnimatePresence mode="wait">{renderCurrentStep()}</AnimatePresence>
        </div>
      </div>

      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <MainButton onClick={handlePrevious} disabled={currentStep === 0} type="secondary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </MainButton>
        </div>

        <div className="flex items-center space-x-3">
          {hasExistingData && (
            <MainButton onClick={handleBackToManager} type="ghostOutlined">
              Go to Manager
            </MainButton>
          )}
          {currentStep === steps.length - 1 ? (
            <MainButton onClick={handleSubmit} type="primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save2 className="w-4 h-4 mr-2" />
                  Save Size Chart
                </>
              )}
            </MainButton>
          ) : (
            <MainButton onClick={handleNext} type="primary">
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </MainButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default SizeChartForm;
